import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Tag, Percent, IndianRupee, Loader2, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { listOffers, saveOffer, removeOffer, errMsg } from "@/lib/api";
import { inr } from "@/lib/format";
import { PageHeader, LocalBanner } from "@/components/common";
import { cn } from "@/lib/utils";

const EMPTY = { code: "", title: "", description: "", discount_type: "percent", discount_value: "", min_order_value: "", active: true, start_date: "", end_date: "" };
const SYNC_NOTE = "Saved to your dashboard account (real backend). These appear in the customer app once your team enables the offers feed.";

function localToday() {
  return new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD in the vendor's local timezone
}

function effectiveState(o) {
  if (!o.active) return { label: "Paused", cls: "text-muted-foreground bg-muted" };
  const today = localToday();
  if (o.start_date && today < o.start_date) return { label: "Scheduled", cls: "text-blue-700 bg-blue-100" };
  if (o.end_date && today > o.end_date) return { label: "Expired", cls: "text-red-700 bg-red-100" };
  return { label: "Live", cls: "text-emerald-700 bg-emerald-100" };
}

function fmtDate(d) {
  if (!d) return null;
  return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

export default function Offers() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["offers"], queryFn: async () => (await listOffers()).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const offers = Array.isArray(data) ? data : [];

  const saveMut = useMutation({
    mutationFn: (body) => saveOffer(body),
    onSuccess: () => { toast.success("Offer saved"); qc.invalidateQueries({ queryKey: ["offers"] }); setOpen(false); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => removeOffer(id),
    onSuccess: () => { toast.success("Offer removed"); qc.invalidateQueries({ queryKey: ["offers"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const submit = () => {
    if (!form.code.trim() || !form.title.trim()) return toast.error("Code and title are required");
    if (!form.discount_value) return toast.error("Enter a discount value");
    if (form.start_date && form.end_date && form.end_date < form.start_date) return toast.error("End date can't be before start date");
    saveMut.mutate({
      ...(form.id ? { id: form.id } : {}),
      code: form.code.trim().toUpperCase(),
      title: form.title.trim(),
      description: form.description,
      discount_type: form.discount_type,
      discount_value: parseFloat(form.discount_value) || 0,
      min_order_value: parseFloat(form.min_order_value) || 0,
      active: form.active,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
    });
  };

  const openEdit = (o) => {
    setForm({
      id: o.id, code: o.code, title: o.title, description: o.description || "",
      discount_type: o.discount_type, discount_value: String(o.discount_value),
      min_order_value: String(o.min_order_value || ""), active: o.active,
      start_date: o.start_date || "", end_date: o.end_date || "",
    });
    setOpen(true);
  };
  const toggle = (o) => saveMut.mutate({ ...o, active: !o.active });

  return (
    <div className="p-5 sm:p-8 max-w-4xl">
      <PageHeader
        title="Offers"
        subtitle="Discount codes — schedule weekend deals to switch on & off automatically"
        action={
          <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="add-offer-btn" className="font-semibold">
            <Plus className="h-4 w-4 mr-1.5" /> New offer
          </Button>
        }
      />
      <LocalBanner className="mb-6" text={SYNC_NOTE} />

      {isLoading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : offers.length === 0 ? (
        <div className="rounded-md border border-dashed border-border py-16 text-center text-muted-foreground">
          No offers yet. Create your first discount code.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {offers.map((o) => {
            const st = effectiveState(o);
            const range = fmtDate(o.start_date) || fmtDate(o.end_date)
              ? `${fmtDate(o.start_date) || "now"} → ${fmtDate(o.end_date) || "no end"}`
              : null;
            return (
              <div key={o.id} className={cn("rounded-md border p-4 relative", o.active ? "border-primary/40 bg-accent/40" : "border-border bg-card opacity-70")} data-testid={`offer-${o.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <button onClick={() => openEdit(o)} className="flex items-center gap-2 text-left" data-testid={`offer-edit-${o.id}`}>
                    <Tag className="h-4 w-4 text-primary" />
                    <span className="font-mono font-bold tracking-wide">{o.code}</span>
                  </button>
                  <div className="flex items-center gap-2">
                    <span className={cn("text-xs font-semibold px-1.5 py-0.5 rounded", st.cls)} data-testid={`offer-state-${o.id}`}>{st.label}</span>
                    <Switch checked={o.active} onCheckedChange={() => toggle(o)} data-testid={`offer-toggle-${o.id}`} />
                  </div>
                </div>
                <p className="font-display font-bold text-lg mt-2 leading-tight">{o.title}</p>
                <p className="text-sm text-primary font-semibold mt-1 flex items-center gap-1">
                  {o.discount_type === "percent" ? <Percent className="h-3.5 w-3.5" /> : <IndianRupee className="h-3.5 w-3.5" />}
                  {o.discount_type === "percent" ? `${o.discount_value}% off` : `${inr(o.discount_value)} off`}
                  {o.min_order_value > 0 && <span className="text-muted-foreground font-normal">· min {inr(o.min_order_value)}</span>}
                </p>
                {range && (
                  <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
                    <CalendarClock className="h-3 w-3" /> {range}
                  </p>
                )}
                {o.description && <p className="text-sm text-muted-foreground mt-1">{o.description}</p>}
                <button onClick={() => delMut.mutate(o.id)} className="absolute bottom-3 right-3 text-muted-foreground hover:text-destructive transition-colors" data-testid={`offer-delete-${o.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="offer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{form.id ? "Edit offer" : "New offer"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Code</Label>
                <Input data-testid="offer-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SAVE20" />
              </div>
              <div className="space-y-1.5">
                <Label>Discount type</Label>
                <div className="flex gap-2">
                  {["percent", "flat"].map((t) => (
                    <button key={t} onClick={() => setForm({ ...form, discount_type: t })} data-testid={`offer-type-${t}`}
                      className={cn("flex-1 h-9 rounded-md border text-sm font-medium capitalize transition-colors", form.discount_type === t ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted")}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input data-testid="offer-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="20% off on all orders" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{form.discount_type === "percent" ? "Discount %" : "Discount ₹"}</Label>
                <Input data-testid="offer-value" type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Min order ₹</Label>
                <Input data-testid="offer-min" type="number" value={form.min_order_value} onChange={(e) => setForm({ ...form, min_order_value: e.target.value })} />
              </div>
            </div>
            {/* Scheduling */}
            <div className="rounded-md border border-border p-3.5">
              <Label className="flex items-center gap-1.5 mb-2"><CalendarClock className="h-3.5 w-3.5" /> Schedule (optional)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Starts</Label>
                  <Input type="date" data-testid="offer-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Ends</Label>
                  <Input type="date" data-testid="offer-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Leave blank to run indefinitely. Offer goes live/expires automatically within this window.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Description (optional)</Label>
              <Textarea data-testid="offer-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saveMut.isPending} data-testid="save-offer-btn" className="font-semibold">
              {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save offer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
